import { ApplicationRoleCode } from '@kcftech/prometheus-client';
import { ErrorBoundary } from '@sentry/react';
import { DocumentTitle } from 'components/DocumentTitle';
import { PersonaModal } from 'components/modal/persona/PersonaModal';
import { NavigationDrawer } from 'components/navigation_drawer/NavigationDrawer';
import { useNavigationDrawerValues } from 'components/navigation_drawer/navigation_drawer_store';
import { TopBar } from 'components/top_bar/TopBar';
import { useRouteEntities } from 'contexts/RouteEntityContext';
import { IThemeContext } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';
import { useNavigationConfig } from 'custom_hooks/useNavigationConfig';
import { MediaQueries } from 'custom_hooks/useWindowSize';
import { ErrorPage } from 'pages/ErrorPage';
import { wrapWithWorkbenchContext } from 'pages/workbench/state';
import { useEffect, useState } from 'react';
import { AppRoutes } from 'routing/AppRoutes';
import { useDebugModeShortcut } from 'shared/debug/useDebugModeShortcut';
import styled, { createGlobalStyle, withTheme } from 'styled-components';
import { tokens } from 'styles/tokens';
import { useIsDashboardView } from 'utils/RouteHelper';

const MainWrapper = styled.div`
    display: flex;
    height: 100%;
    overflow: hidden;
    transition: background 0.2s linear;

    @media ${MediaQueries.smallAndDown} {
        position: relative;
    }
`;

const NavigationDrawerAppWrapper = styled.div`
    display: flex;
    flex-grow: 1;
`;

const TopBarAndContentWrapper = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    width: 100%;

    @media ${MediaQueries.smallAndDown} {
        position: absolute;
    }
`;

const ContentWrapper = styled.div`
    flex-grow: 1;
    overflow-y: auto;
    overflow-x: hidden;
`;

const GlobalStyle = createGlobalStyle<{ colorMode: string }>`
    :root {
        color-scheme: ${({ colorMode }) => colorMode};
        --kendo-text-color: ${tokens.text.primary};
        --kendo-background-color: ${tokens.background.paperElevation0};
        --kendo-border-color: ${tokens.border.default};
        --kendo-background-hover: ${tokens.action.hover};
    }
    body {
        color: ${tokens.text.primary};
    }
`;

// MainPage is wrapped with "withTheme", so it receives the current theme
type MainPageProps = {
    theme?: IThemeContext;
};

function MainPageImpl({ theme }: MainPageProps) {
    const [personaModalClosed, setPersonaModalClosed] = useState(false);

    useDebugModeShortcut();
    useNavigationConfig();

    const userContext = useUser();
    const { corporation } = useRouteEntities();

    const { isNavbarHidden } = useNavigationDrawerValues((state) => ({
        isNavbarHidden: state.isHidden,
    }));

    const showTaskBar =
        corporation !== undefined ||
        userContext.user?.ApplicationRole === ApplicationRoleCode.SystemAdmin ||
        userContext.user?.ApplicationRole === ApplicationRoleCode.Sentry ||
        userContext.user?.ApplicationRole === ApplicationRoleCode.Sales;

    const showPendoBadge = useIsDashboardView();

    useEffect(() => {
        if (showPendoBadge) {
            document.body.classList.add('hide-pendo-badge');
        } else {
            document.body.classList.remove('hide-pendo-badge');
        }
    }, [showPendoBadge]);

    return (
        <>
            <DocumentTitle title="SMARTdiagnostics" />
            <MainWrapper className={`theme-${theme?.colorMode ?? 'light'}`}>
                {!isNavbarHidden && (
                    <NavigationDrawerAppWrapper>
                        {userContext.token && showTaskBar && <NavigationDrawer />}
                    </NavigationDrawerAppWrapper>
                )}
                <TopBarAndContentWrapper>
                    {userContext.token && <TopBar />}
                    {/* This is where contextual modals will spawn in the app.
                        They are limited to existing under the top bar, and in
                        the general app area.
                    */}
                    <ContentWrapper id="contextual-modal-root">
                        <ErrorBoundary fallback={ErrorPage}>
                            <AppRoutes />
                            {!personaModalClosed &&
                                userContext.user &&
                                userContext.user.Profile.Persona === undefined && (
                                    <PersonaModal
                                        profile={userContext.user.Profile}
                                        onSubmit={() => {
                                            userContext.reload();
                                            setPersonaModalClosed(true);
                                        }}
                                    />
                                )}
                        </ErrorBoundary>
                    </ContentWrapper>
                </TopBarAndContentWrapper>
            </MainWrapper>
            <GlobalStyle colorMode={theme?.colorMode ?? 'light'} />
        </>
    );
}
export const MainPage = wrapWithWorkbenchContext(withTheme(MainPageImpl));
